<script>
  import AppButton from "../../lib/components/Button.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import Modal from "@components/Modal.svelte";
  import { Button, InfoBlock, TextInput } from "paxray-components-svelte";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";
  import {
    findCustomMatch,
    findPresetMatch,
    findPresetSuggestions,
    presetUrlToDomain,
    websiteDisplayName,
    websiteMatches,
  } from "lib/util/restrictionRules.js";

  export let currentStep, stepsCompleted, idproject, firstOpen;

  let explanationModalOpen = false;
  let submitModalOpen = false;
  let blacklistWebsites = [];
  let newItemModalOpen = false;
  let currentWebsite = {};
  let currentlistid = 0;
  let editId = -1;
  let presetModalOpen = false;
  let presetPagination = { page: 1, pageSize: 10 };
  let presetSearchTerm = "";
  let expandedRowIds = [];
  let showHowto = false;
  let howtoStep = 0;

  const howtoSteps = [
    {
      image: "img/userinput/websites/screen1.png",
      text: "Open the website in your browser",
    },
    {
      image: "img/userinput/websites/screen2.png",
      text: "Copy the address from the address bar, e.g. amazon.com",
    },
    {
      image: "img/userinput/websites/screen3.png",
      text: "To exclude only one page, copy its full address as Sub-URL, e.g. amazon.com/music",
    },
  ];

  $: isLastHowtoStep = howtoStep === howtoSteps.length - 1;

  function openHowto() {
    howtoStep = 0;
    showHowto = true;
  }

  function nextHowtoStep() {
    if (isLastHowtoStep) showHowto = false;
    else howtoStep += 1;
  }

  function previousHowtoStep() {
    if (howtoStep === 0) showHowto = false;
    else howtoStep -= 1;
  }

  fetchWrapper(`/api/damprojects/getUserLimitations/Blacklist`)
    .then((response) => response.json())
    .then((data) => {
      blacklistWebsites = normalizeRows(data.webapp);
    });

  function normalizeRows(rows) {
    return (rows || []).map((row) => ({
      ...row,
      user: Array.isArray(row.user) ? row.user : [],
      preset: Array.isArray(row.preset) ? row.preset : [],
    }));
  }

  function addToBlacklist(id = 0) {
    if (!blacklistWebsites[id]) return;
    newItemModalOpen = true;
    currentlistid = id;
    editId = -1;
    urlerror = false;
    showHowto = false;
    currentWebsite = {
      limitationPresetId: blacklistWebsites[id]["id"],
      editable: true,
      id: blacklistWebsites[id].user.length,
      name: "",
      url: "",
      suburl: "",
      reason: "",
    };
  }

  let urlerror = false;

  $: presetMatch = findPresetMatch(
    blacklistWebsites,
    currentWebsite.url,
    websiteMatches,
  );
  $: customDuplicate = findCustomMatch(
    blacklistWebsites,
    currentWebsite.url,
    websiteMatches,
    { listId: currentlistid, ruleId: editId },
  );
  $: websiteSuggestions = findPresetSuggestions(
    blacklistWebsites,
    currentWebsite.url,
    (preset) => presetUrlToDomain(preset.url),
  );

  function applySuggestion(suggestion) {
    currentWebsite.url = suggestion.value;
    currentWebsite.name = suggestion.value;
  }

  function saveNewWebsite() {
    urlerror = !currentWebsite.url;
    if (urlerror || customDuplicate) return;

    currentWebsite.name =
      currentWebsite.name || websiteDisplayName(currentWebsite.url);
    currentWebsite.suburl = currentWebsite.suburl || "";
    currentWebsite.reason = currentWebsite.reason || "";

    if (editId == -1)
      blacklistWebsites[currentlistid]["user"].push(currentWebsite);
    else blacklistWebsites[currentlistid]["user"][editId] = currentWebsite;
    blacklistWebsites = blacklistWebsites;
    newItemModalOpen = false;
    expandedRowIds = ["project-website-exclusions"];
  }

  function editUserWebsite(listId, id) {
    currentlistid = listId;
    editId = id;
    urlerror = false;
    showHowto = false;
    currentWebsite = blacklistWebsites[currentlistid]["user"][editId];
    newItemModalOpen = true;
  }

  function deleteFromBlackList(list, id) {
    list.splice(id, 1);
    let indexcounter = 0;
    list.forEach((element) => {
      element.id = indexcounter;
      indexcounter++;
    });
    list = list;
    blacklistWebsites = blacklistWebsites;
  }

  function submitBlacklist() {
    fetchWrapper(`/api/damprojects/saveUserLimitations/${idproject}/black`, {
      method: "POST",
      body: JSON.stringify(blacklistWebsites),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status == "success") {
          currentStep = 2;
          stepsCompleted = true;
          submitModalOpen = false;
        }
      });
  }

  function includesSearch(value, searchTerm) {
    return String(value || "")
      .toLowerCase()
      .includes(searchTerm);
  }

  $: presetRows = blacklistWebsites.flatMap((row, sourceIndex) =>
    (row.preset || []).map((preset, presetIndex) => ({
      ...preset,
      id: `${sourceIndex}-${presetIndex}`,
      category: row.name,
    })),
  );
  $: customRows = blacklistWebsites.flatMap((row, sourceIndex) =>
    (row.user || []).map((rule) => ({
      ...rule,
      id: `${sourceIndex}-${rule.id}`,
      sourceIndex,
      sourceId: rule.id,
    })),
  );
  $: projectRows = [
    {
      id: "project-website-exclusions",
      name: "Custom Website Exclusions",
      description:
        "Add websites or web apps that should be excluded for this project.",
      presets: presetRows.length,
      edit: true,
    },
  ];
  $: presetGroups = blacklistWebsites.map((row, sourceIndex) => ({
    ...row,
    id: `${sourceIndex}`,
    presetCount: (row.preset || []).length,
    presetRows: (row.preset || []).map((preset, presetIndex) => ({
      ...preset,
      id: `${sourceIndex}-${presetIndex}`,
    })),
  }));
  $: normalizedPresetSearchTerm = presetSearchTerm.trim().toLowerCase();
  $: filteredPresetGroups = presetGroups.filter((group) => {
    if (!normalizedPresetSearchTerm) return true;

    return (
      includesSearch(group.name, normalizedPresetSearchTerm) ||
      includesSearch(group.description, normalizedPresetSearchTerm) ||
      group.presetRows.some((preset) =>
        Object.values(preset).some((value) =>
          includesSearch(value, normalizedPresetSearchTerm),
        ),
      )
    );
  });
</script>

<Modal
  bind:open={submitModalOpen}
  modalHeading="Submit Entries"
  primaryButtonText="Submit Excluded Websites"
  secondaryButtonText="Cancel"
  secondaryButtonAction={() => (submitModalOpen = false)}
  primaryButtonAction={submitBlacklist}
  on:close={() => (submitModalOpen = false)}
  actions
  autowidth
>
  <p>Do you want to submit your entries?</p>
  <p>This is a permanent action and cannot be undone.</p>
</Modal>

<Modal
  open={firstOpen || explanationModalOpen}
  modalHeading="First Steps"
  primaryButtonText="Okay"
  preventCloseOnClickOutside
  noSecondaryButton
  primaryButtonAction={() => {
    explanationModalOpen = false;
    firstOpen = false;
  }}
  on:close={() => {
    explanationModalOpen = false;
    firstOpen = false;
  }}
  actions
>
  <h5>
    In this example we show you how to add the website "Amazon Music" to the
    blacklist
  </h5>
  <br /><br /><br />
  <video style="width:100%" controls>
    <track kind="captions" /><track />
    <source src="/img/userinput/websites/explanation.mp4" type="video/mp4" />
    Your browser does not support the video tag.
  </video>
</Modal>

<Modal
  bind:open={presetModalOpen}
  modalHeading="Website Presets"
  primaryButtonText="Okay"
  preventCloseOnClickOutside
  noSecondaryButton
  primaryButtonAction={() => (presetModalOpen = false)}
  on:close={() => (presetModalOpen = false)}
  width="auto"
  actions
>
  <DataTableBlack
    expandable
    headers={[
      { key: "name", value: "Name" },
      { key: "description", value: "Description" },
      { key: "presetCount", value: "Presets" },
    ]}
    rows={filteredPresetGroups}
    bind:page={presetPagination.page}
    bind:pageSize={presetPagination.pageSize}
    totalItems={filteredPresetGroups.length}
    pageSizes={[10, 25, 50, 100]}
    searchValue={presetSearchTerm}
    on:search={(e) => {
      presetSearchTerm = e.detail.value;
    }}
    showNewButton={false}
  >
    <svelte:fragment slot="expanded-row" let:row>
      <DataTableBlack
        headers={[
          { key: "name", value: "Name" },
          { key: "url", value: "URL" },
          { key: "suburl", value: "Sub-URL" },
          { key: "reason", value: "Reason" },
        ]}
        rows={row.presetRows}
        showToolbar={false}
        showPagination={false}
      >
        <svelte:fragment slot="cell" let:cell>
          {cell.value || ""}
        </svelte:fragment>
      </DataTableBlack>
    </svelte:fragment>
  </DataTableBlack>
</Modal>

<!-- <div class="information_fields">
  <div class="information_field_top">
    Here, you can limit the initial scope of the paxray sensor by defining
    exclusions for websites and web apps.
    <Help20
      style="float:right; cursor:pointer; color:#FF5F1F"
      on:click={() => {
        explanationModalOpen = true;
      }}
    />
  </div>
  <div class="information_field_bottom">
    You will find lists of blacklist presets. You can add more websites quickly
    with a single mouse click. To view the blacklisted websites, click on the
    arrow to expand the list.
    <br />
    <br />
    Click on the respective <span>+</span> to add your own exclusions and follow
    the displayed introductions.
    <br />
    <br />
    Submit your input by clicking on <span>submit</span> in the bottom-right corner
    to proceed to the next step.
  </div>
</div> -->

<DataTableBlack
  expandable
  headers={[
    { key: "name", value: "Name" },
    { key: "description", value: "Description" },
    { key: "presets", value: "Presets" },
    { key: "edit", value: "Actions" },
  ]}
  rows={projectRows}
  bind:expandedRowIds
  showToolbar={false}
  showPagination={false}
>
  <svelte:fragment slot="cell" let:cell let:row>
    {#if cell.key == "edit"}
      <div class="action_cell">
        <AppButton
          variant="secondary"
          size="sm"
          text="Add Website"
          on:click={() => addToBlacklist(0)}
        />
      </div>
    {:else if cell.key == "presets"}
      <span data-guide="restrictions-presets">
        <AppButton
          variant="secondary"
          size="sm"
          text="{row.presets} Website Presets"
          on:click={() => (presetModalOpen = true)}
        />
      </span>
    {:else}
      {cell.value}
    {/if}
  </svelte:fragment>
  <svelte:fragment slot="expanded-row" let:row>
    <DataTableBlack
      headers={[
        { key: "name", value: "Name" },
        { key: "url", value: "URL" },
        { key: "suburl", value: "Sub-URL" },
        { key: "reason", value: "Reason" },
        { key: "editable", value: "Actions" },
      ]}
      rows={customRows}
      showToolbar={false}
      showPagination={false}
    >
      <svelte:fragment slot="cell" let:cell let:row={innerrow}>
        {#if cell.key == "editable"}
          <div class="action_cell">
            <AppButton
              variant="secondary"
              size="sm"
              text="Edit"
              on:click={() => {
                editUserWebsite(innerrow.sourceIndex, innerrow.sourceId);
              }}
            />
            <AppButton
              variant="secondary"
              size="sm"
              text="Delete"
              on:click={() => {
                deleteFromBlackList(
                  blacklistWebsites[innerrow.sourceIndex].user,
                  innerrow.sourceId,
                );
              }}
            />
          </div>
        {:else}
          {cell.value || ""}
        {/if}
      </svelte:fragment>
    </DataTableBlack>
  </svelte:fragment>
</DataTableBlack>

<div class="button_wrapper" data-guide="restrictions-submit">
  <div></div>
  <AppButton
    variant="secondary"
    text="Submit Excluded Websites"
    on:click={() => (submitModalOpen = true)}
  />
</div>

<Modal
  bind:open={newItemModalOpen}
  modalHeading={showHowto ? "How to find the URL" : "Add Website"}
  preventCloseOnClickOutside
  noSecondaryButton
  primaryButtonText={showHowto ? (isLastHowtoStep ? "Got it" : "Next") : "Save"}
  primaryButtonAction={showHowto ? nextHowtoStep : saveNewWebsite}
  primaryButtonVariant="secondary"
  on:close={() => (newItemModalOpen = false)}
  width="auto"
  actions
  xxl
>
  {#if showHowto}
    <div class="howto">
      <img
        class="howto_image"
        src={howtoSteps[howtoStep].image}
        alt={howtoSteps[howtoStep].text}
      />
      <div class="howto_text">
        <p class="howto_step">Step {howtoStep + 1} of {howtoSteps.length}</p>
        <p>{howtoSteps[howtoStep].text}</p>
      </div>
    </div>
  {:else}
    <div class="blacklist_website_modal">
      <InfoBlock
        type="info"
        heading="Which website?"
        message="This website will be explicitly excluded from all future data collection. Enter its address or type its name to pick a suggestion."
        showClose={false}
      />

      <div class="input_wrapper">
        <div class="rule_input">
          <div class="rule_input_row">
            <div class="rule_input_field">
              <TextInput
                invalid={(urlerror && !currentWebsite.url) || customDuplicate}
                invalidText={customDuplicate
                  ? "This website is already in your custom rules"
                  : "Please specify the website URL"}
                labelText="Website URL (required)"
                placeholder="Enter website URL or search presets, e.g. amazon.com"
                description={presetMatch
                  ? `Also covered by the preset list ${presetMatch.group.name}. This rule keeps it excluded even if the preset is disabled.`
                  : null}
                bind:value={currentWebsite.url}
              />
            </div>
            <div class="howto_button">
              <Button kind="secondary" on:click={openHowto}>
                How do I find the URL?
              </Button>
            </div>
          </div>
          {#if websiteSuggestions.length}
            <div class="rule_suggestions">
              {#each websiteSuggestions as suggestion}
                <AppButton
                  variant="secondary"
                  size="sm"
                  text="{suggestion.value} ({suggestion.group})"
                  on:click={() => applySuggestion(suggestion)}
                />
              {/each}
            </div>
          {/if}
        </div>
        <TextInput
          labelText="Display Name (optional)"
          placeholder="Shown in the rule list, e.g. Amazon Music"
          bind:value={currentWebsite.name}
        />
        <TextInput
          labelText="Sub-URL (optional)"
          placeholder="Sub-URL of the page, e.g. amazon.com/music"
          bind:value={currentWebsite.suburl}
        />
        <TextInput
          labelText="Reason (optional)"
          placeholder="Short reason for this rule, e.g. music streaming"
          bind:value={currentWebsite.reason}
        />
      </div>
    </div>
  {/if}
  <div slot="left-buttons">
    {#if showHowto}
      <AppButton
        variant="secondary"
        text={howtoStep === 0 ? "Back to form" : "Back"}
        on:click={previousHowtoStep}
      />
    {:else}
      <AppButton
        variant="secondary"
        text="Cancel"
        on:click={() => (newItemModalOpen = false)}
      />
    {/if}
  </div>
</Modal>

<style>
  .button_wrapper {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  :global(.bx--data-table th:last-child),
  :global(.bx--data-table td:last-child) {
    padding-right: 1rem;
    text-align: right;
  }

  :global(.bx--data-table th:last-child .bx--table-header-label) {
    display: flex;
    justify-content: flex-end;
  }

  :global(.bx--data-table td:last-child .button-secondary) {
    display: flex;
  }

  .action_cell {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .blacklist_website_modal > :global(* + *) {
    margin-top: var(--control-gap);
  }

  .input_wrapper {
    display: flex;
    flex-direction: column;
    background-color: var(--pax-white);
    gap: var(--control-gap);
  }

  .rule_suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--control-gap);
    margin-top: var(--control-gap);
  }

  .rule_input_row {
    display: flex;
    align-items: flex-start;
    gap: var(--control-gap);
  }

  .rule_input_field {
    flex: 1;
    min-width: 0;
  }

  /* Aligns the button with the input, below the field label. */
  .howto_button {
    margin-top: 1.5rem;
  }

  .howto {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .howto_image {
    align-self: center;
    width: auto;
    max-width: 100%;
    /* Modal is capped at 84% height, 18rem covers header, step text and footer. */
    max-height: calc(84vh - 18rem);
    border: 1px solid var(--pax-black-inactive);
    border-radius: 0.5rem;
  }

  .howto_text {
    text-align: center;
  }

  .howto_text p {
    margin: 0;
  }

  .howto_step {
    font-weight: 600;
  }
</style>
